import { customFetcher } from '@/shared/api/client';
import { getApi } from '@/shared/api';
import type { PostApiFilesUpload201 } from '@/shared/api/models';

export type MarketingContentAttachment = {
    id: number;
    url: string;
    fileName: string;
    createdAt: string;
    fileSize?: number;
};

const BAHLOU_ADMIN_HOSTNAME = 'admin.bahlou.dk';

export const getMarketingAttachmentsDirectoryPath = (): string => {
    if (typeof window !== 'undefined' && window.location.hostname === BAHLOU_ADMIN_HOSTNAME) {
        return 'bahlou/marketing-attachments';
    }
    return 'fiind/marketing-attachments';
};

export const getMarketingAttachmentHref = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
        return url;
    }
    return `${process.env.REACT_APP_IMG_URL || ''}${url}`;
};

export const uploadMarketingAttachmentsApi = async (
    files: File[],
): Promise<MarketingContentAttachment[]> => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file);
    });
    formData.append('directoryPath', getMarketingAttachmentsDirectoryPath());

    // Orval client does not accept multipart body; call mutator with FormData directly.
    const response = await customFetcher<PostApiFilesUpload201>({
        url: '/api/files/upload',
        method: 'POST',
        data: formData,
    });

    const uploaded = response?.files ?? [];
    return uploaded
        .filter((item) => item?.id != null && item?.url)
        .map((item, index) => ({
            id: item.id,
            url: item.url,
            fileName: item.fileName,
            createdAt: item.createdAt,
            fileSize: files[index]?.size ?? 0,
        }));
};

export const deleteMarketingAttachmentsApi = async (ids: number[]) => {
    if (!ids.length) return;
    const api = getApi();
    return api.deleteApiFiles({ ids });
};

export const normalizeMarketingAttachments = (value: unknown): MarketingContentAttachment[] => {
    if (!Array.isArray(value)) return [];

    return value
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const record = item as Record<string, unknown>;
            const id = Number(record.id);
            const url = typeof record.url === 'string' ? record.url : '';
            const fileName =
                typeof record.fileName === 'string'
                    ? record.fileName
                    : typeof record.file_name === 'string'
                      ? record.file_name
                      : url.split('/').pop() || 'file';
            const createdAt =
                typeof record.createdAt === 'string'
                    ? record.createdAt
                    : typeof record.created_at === 'string'
                      ? record.created_at
                      : '';
            const fileSizeRaw = record.fileSize ?? record.file_size;
            const fileSize = fileSizeRaw != null ? Number(fileSizeRaw) : undefined;

            if (!Number.isFinite(id) || !url) return null;
            return {
                id,
                url,
                fileName,
                createdAt,
                ...(Number.isFinite(fileSize) && fileSize > 0 ? { fileSize } : {}),
            };
        })
        .filter((item): item is MarketingContentAttachment => item != null);
};
